import React, { useMemo, useState } from "react";
import LineChart from "@cloudscape-design/components/line-chart";
import Box from "@cloudscape-design/components/box";

const PerformanceChart = ({
  title,
  data,
  height = 300,
  legend,
  timeRange,
  isExpanded = false,
}) => {
  const [focusedSeries, setFocusedSeries] = useState(null);
  const legendArray = Array.isArray(legend) ? legend : [legend];
  const handleSeriesToggle = (seriesTitle) => {
    setFocusedSeries(focusedSeries === seriesTitle ? null : seriesTitle);
  };
  const valueFormatter = (value) => {
    if (value === 0) return "0";
    if (isNaN(value)) return "0";

    if (Math.abs(value) >= 1e9)
      return `${(value / 1e9).toFixed(1).replace(/\.0$/, "")}G`;
    if (Math.abs(value) >= 1e6)
      return `${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
    if (Math.abs(value) >= 1e3)
      return `${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
    if (Math.abs(value) >= 1) {
      return value % 1 === 0 ? value.toString() : value.toFixed(1);
    }
    if (Math.abs(value) >= 0.1) {
      return value.toFixed(2);
    }
    if (Math.abs(value) >= 0.01) {
      return value.toFixed(2);
    }
    return value.toFixed(3);
  };
  const { chartData, xTicks, yTicks, xDomain, yDomain, unit } = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        chartData: {},
        xTicks: [],
        yTicks: [],
        xDomain: [new Date(), new Date()],
        yDomain: [0, 1],
        unit: "",
      };
    }
    const timeMap = new Map();
    let minValue = Infinity;
    let maxValue = -Infinity;
    let unit = data[0]?.unit || "";
    data.forEach((item) => {
      if (!item?.timestamp || !item?.resourceName) return;

      const timestamp = new Date(item.timestamp).getTime();
      const value = parseFloat(item.value) || 0;

      if (!timeMap.has(timestamp)) {
        timeMap.set(timestamp, {
          timestamp,
          date: new Date(timestamp),
        });
      }

      timeMap.get(timestamp)[item.resourceName] = value;

      if (value < minValue) minValue = value;
      if (value > maxValue) maxValue = value;
    });

    const sortedData = Array.from(timeMap.values()).sort(
      (a, b) => a.timestamp - b.timestamp
    );
    if (sortedData.length === 0) {
      return {
        chartData: {},
        xTicks: [],
        yTicks: [],
        xDomain: [new Date(), new Date()],
        yDomain: [0, 1],
        unit: "",
      };
    }

    const startTime = sortedData[0].date;
    const endTime = sortedData[sortedData.length - 1].date;
    const timeSpan = endTime - startTime;

    let tickCount = 6;
    if (timeRange === "1h") tickCount = 5;
    else if (timeRange === "3h") tickCount = 7;
    else if (timeRange === "12h" || timeRange === "1d") tickCount = 5;
    else if (timeRange === "3d") tickCount = 7;
    else if (timeRange === "1w") tickCount = 8;
    else if (timeRange.startsWith("custom:")) {
      const diffDays = timeSpan / (1000 * 60 * 60 * 24);
      tickCount = Math.min(Math.ceil(diffDays) + 1, 14);
    }

    const xTicks = [];
    for (let i = 0; i < tickCount; i++) {
      const tickTime = new Date(
        startTime.getTime() + i * (timeSpan / (tickCount - 1))
      );
      xTicks.push(tickTime);
    }

    const yMin = 0;
    const yRange = maxValue - minValue;
    let yMax;

    if (yRange < 0.1) {
      yMax = Math.ceil(maxValue * 1.2 * 1000) / 1000;
    } else if (yRange < 1) {
      yMax = Math.ceil(maxValue * 1.2 * 100) / 100;
    } else if (yRange < 10) {
      yMax = Math.ceil(maxValue * 1.15 * 10) / 10;
    } else if (yRange < 100) {
      yMax = Math.ceil(maxValue * 1.15);
    } else {
      const roundTo = Math.pow(10, Math.floor(Math.log10(yRange)) - 1);
      yMax = Math.ceil((maxValue * 1.1) / roundTo) * roundTo;
    }

    const yTickCount = yRange < 0.1 ? 4 : yRange > 1000 ? 6 : 5;
    const yTicks = [];
    for (let i = 0; i <= yTickCount; i++) {
      const tickValue = yMin + (i * (yMax - yMin)) / yTickCount;
      let roundedTick;

      if (yRange < 0.01) roundedTick = Math.round(tickValue * 1000) / 1000;
      else if (yRange < 0.1) roundedTick = Math.round(tickValue * 100) / 100;
      else if (yRange < 1) roundedTick = Math.round(tickValue * 10) / 10;
      else roundedTick = Math.round(tickValue);

      yTicks.push(roundedTick);
    }

    return {
      chartData: sortedData,
      xTicks,
      yTicks,
      xDomain: [startTime, endTime],
      yDomain: [yMin, yMax],
      unit,
    };
  }, [data, timeRange]);

  const seriesData = useMemo(() => {
    if (!chartData || Object.keys(chartData).length === 0) return [];

    const colorPalette = [
      "#2E75B6",
      "#ED7D31",
      "#70AD47",
      "#C0504D",
      "#9B59B6",
      "#4BACC6",
      "#F1C232",
      "#8064A2",
      "#31869B",
      "#E36C09",
    ];
    const lineStyles = ["solid", "dashed", "dotted", "solid", "dashed"];
    const markerShapes = ["circle", "square", "diamond", "triangle", "cross"];

    const samplePoints = (points) => {
      if (points.length <= 50) return points;

      const samplingRate = Math.ceil(points.length / 50);
      const result = [];

      result.push(points[0]);

      let minPoint = points[0];
      let maxPoint = points[0];
      points.forEach((point) => {
        if (point.y < minPoint.y) minPoint = point;
        if (point.y > maxPoint.y) maxPoint = point;
      });

      for (let i = 1; i < points.length - 1; i += samplingRate) {
        result.push(points[i]);
      }

      result.push(points[points.length - 1]);

      if (!result.some((p) => p.x.getTime() === minPoint.x.getTime())) {
        result.push(minPoint);
      }
      if (!result.some((p) => p.x.getTime() === maxPoint.x.getTime())) {
        result.push(maxPoint);
      }

      return result.sort((a, b) => a.x - b.x);
    };

    return legendArray
      .map((resourceName, index) => {
        if (focusedSeries !== null && focusedSeries !== resourceName) {
          return null;
        }

        const seriesPoints = chartData.map((item) => ({
          x: item.date,
          y: parseFloat(item[resourceName] || 0),
        }));

        const optimizedPoints = samplePoints(seriesPoints);

        return {
          title: resourceName,
          type: "line",
          color: colorPalette[index % colorPalette.length],
          data: optimizedPoints,
          style: lineStyles[index % lineStyles.length],
          markerType: markerShapes[index % markerShapes.length],
          valueFormatter: valueFormatter,
          thickness: 2,
        };
      })
      .filter(Boolean);
  }, [chartData, legendArray, focusedSeries]);

  if (!data || data.length === 0) {
    return (
      <div style={{ minHeight: `${height+100}px` }} className="flex items-center justify-center border border-gray-200 rounded">
        <Box textAlign="center" color="inherit" padding="l">
          <b>No data available</b>
          <Box variant="p" color="inherit">
            There is no data available for this metric
          </Box>
        </Box>
      </div>
    );
  }

  const LegendItem = ({ series, isVisible, isFocused, onClick }) => (
    <div className="flex items-center cursor-pointer " onClick={onClick}>
      {isExpanded ? (
        <div className="flex flex-row items-center break-all">
          <div
            className={`w-2 h-2 mr-2 p-2 rounded-full transition-all ${isFocused ? "scale-100" : ""}`}
            style={{
              backgroundColor: series.color,
              opacity: isVisible ? 1 : 0.3,
              transition: "transform 0.2s, opacity 0.2s",
            }}
          />

          <div
            className={`flex text-xs transition-opacity ${
              isVisible
                ? isFocused
                  ? "text-gray-800 font-medium"
                  : "text-gray-600"
                : "text-gray-400"
            }`}
          >
            {series.title}
          </div>
        </div>
      ) : (
        <div
          className={`w-2 h-2 mr-2 p-2 rounded-full transition-all ${isFocused ? "scale-100" : ""}`}
          style={{
            backgroundColor: series.color,
            opacity: isVisible ? 1 : 0.3,
            transition: "transform 0.2s, opacity 0.2s",
          }}
        />
      )}
    </div>
  );

  const Legend = ({ series }) =>
    isExpanded ? (
      <div
        className="flex flex-col gap-3 m-2 mt-10 p-1 justify-left overflow-y-auto"
        style={{
          maxHeight: `${height}px`,
          height: `${height}px`,
          minWidth: "120px",
        }}
      >
        {series.map((s, i) => (
          <LegendItem
            key={i}
            series={s}
            isVisible={focusedSeries === null || focusedSeries === s.title}
            isFocused={focusedSeries === s.title || focusedSeries === null}
            onClick={() => handleSeriesToggle(s.title)}
          />
        ))}
      </div>
    ) : (
      <div className="flex flex-wrap gap-3 mt-2 justify-left">
        {series.map((s, i) => (
          <LegendItem
            key={i}
            series={s}
            isVisible={focusedSeries === null || focusedSeries === s.title}
            isFocused={focusedSeries === s.title || focusedSeries === null}
            onClick={() => handleSeriesToggle(s.title)}
          />
        ))}
      </div>
    );

  return (
    <div
      className={
        isExpanded ? "relative flex flex-row " : "relative flex flex-col "
      }
    >
      <div className={isExpanded ? "w-4/5" : ""}>
        <LineChart
          series={seriesData}
          xDomain={xDomain}
          yDomain={yDomain}
          xTicks={xTicks}
          yTicks={yTicks}
          curves
          highlightedSeries={null}
          horizontalGridLines
          i18nStrings={{
            xTickFormatter: (e) =>
              e
                .toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: false,
                })
                .split(",")
                .join("\n"),
            yTickFormatter: valueFormatter,
            legendAriaLabel: "Resource Names",
            chartAriaRoleDescription: "Performance metrics line chart",
          }}
          ariaLabel={`${title} line chart`}
          height={height}
          hideFilter
          xScaleType="time"
          xTitle="Time (UTC)"
          yTitle={unit}
          hideLegend={true}
          statusType="finished"
          empty={
            <Box textAlign="center" color="inherit">
              <b>No data available</b>
              <Box variant="p" color="inherit">
                There is no data available for this metric
              </Box>
            </Box>
          }
          noMatch={
            <Box textAlign="center" color="inherit">
              <b>No matching data</b>
              <Box variant="p" color="inherit">
                There is no matching data to display
              </Box>
            </Box>
          }
          detailPopoverSize="large"
          detailPopoverFooter={(popoverProps) => {
            const date = popoverProps?.x;
            if (date && date instanceof Date && !isNaN(date)) {
              return (
                <span className="text-xs">
                  {date.toLocaleString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric",
                    hour12: false,
                  })}
                </span>
              );
            }
          }}
          detailPopoverSeriesContent={(props) => ({
            key: (
              <span className="text-xs" style={{ color: props.series.color }}>
                {props.series.title}
              </span>
            ),
            value: <span className="text-xs">{valueFormatter(props.y)}</span>,
          })}
        />
      </div>
      <div
        className={
          isExpanded
            ? "w-1/5 flex justify-center rounded-md ml-1 p-1"
            : ""
        }
        style={isExpanded ? { height: `${height}px` } : {}}
      >
        <Legend
          series={legendArray.map((name, index) => ({
            title: name,
            color:
              seriesData.find((s) => s?.title === name)?.color ||
              ["#2E75B6", "#ED7D31", "#70AD47", "#C0504D", "#9B59B6"][
                index % 5
              ],
          }))}
        />
      </div>
    </div>
  );
};

export default PerformanceChart;